import { useFormik } from 'formik'
import qs from 'qs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Alert, Button, Card, CardBody, Col, Container, Form, FormFeedback, Input, Label, Row, Spinner } from 'reactstrap'
import * as Yup from 'yup'
import auth from '../../api/auth/hooks'
import logoLight from '../../assets/images/dimedia-crm-logo-white.png'
import { selectErrorMessage, selectIsAuthenticated, selectResetPasswordSuccess } from '../../store/auth'
import AuthLayout from './AuthLayout'
import ObscurePassword from './ObscurePassword'

const Login = () => {
    const location = useLocation()
    const history = useHistory()
    const { t } = useTranslation()

    const [obscurePassword, setObscurePassword] = useState(true)

    const { isAuthenticated, errorMessage, isSuccess } = useSelector((state) => {
        return {
            isAuthenticated: selectIsAuthenticated(state),
            errorMessage: selectErrorMessage(state),
            isSuccess: selectResetPasswordSuccess(state),
        }
    })

    const { data: agentHomepage } = auth.CurrentAgent.useQuery({ 
        enabled: isAuthenticated, 
        select: (data) => data.agentHomepage 
    })
    const { mutate: login, isPending, error: loginError } = auth.Login.useMutation()

    // loginError (a failed submit) is always fresher than errorMessage (a stale session_expired
    // carried over in redux from a redirect that happened before this mutation ever ran)
    const activeError = loginError ? { status: loginError.response?.status, message: loginError.response?.data?.message } : errorMessage

    // Add more server error messages as needed
    const AUTH_ERROR_MESSAGES: Record<string, string> = {
        'Neispravni akreditacijski podaci.': 'form.login.error.unauthorized',
        'Previše neuspjelih pokušaja prijave, pokušajte ponovo za 5 minuta.': 'form.login.error.requests',
        session_expired: 'form.login.error.sessionExpired',
    }

    const handleAuthenticated = useCallback(() => {
        const params = qs.parse(location.search, { ignoreQueryPrefix: true })
        const homepage = agentHomepage || '/properties'
        const redirectUrl = typeof params.continue === 'string' && params.continue !== '/' ? params.continue : homepage

        history.replace(redirectUrl)
    }, [history, location.search, agentHomepage])

    const handleErrorMessage = (message: string) => {
        return t(AUTH_ERROR_MESSAGES[message])
    }

    useEffect(() => {
        if (isAuthenticated) {
            handleAuthenticated()
        }
    }, [handleAuthenticated, isAuthenticated])

    const formik = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,
        initialValues: {
            username: '',
            password: '',
        },
        validationSchema: Yup.object({
            username: Yup.string().required(t('form.login.error.username')).email(t('form.register.error.invalidEmail')),
            password: Yup.string().required(t('app.common.password.error')),
        }),
        onSubmit: (values) => {
            login({ username: values.username, password: values.password })
        },
    })

    return (
        <AuthLayout>
            <div className="auth-page-content">
                <Container>
                    <Row className="mt-4">
                        <Col lg={12}>
                            <div className="text-center mt-5 mb-5  text-white-50">
                                <div>
                                    <Link to="/" className="d-inline-block auth-logo">
                                        <img src={logoLight} alt="" />
                                    </Link>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row className="justify-content-center">
                        <Col md={8} lg={6} xl={5}>
                            <Card className="mt-5">
                                <CardBody className="p-4">
                                    <div className="text-center mt-2">
                                        <h5 className="fs-16">{t('form.login.title')}</h5>
                                        <p className="text-muted">{t('form.login.message')}</p>
                                    </div>

                                    {isSuccess ? (
                                        <Alert color="success" style={{ marginTop: '13px' }}>
                                            {t('form.resetPassword.success.message')}
                                        </Alert>
                                    ) : null}

                                    {activeError?.status === 401 ? (
                                        <Alert color="danger" className="text-center" fade={false}>
                                            {handleErrorMessage(activeError.message)}
                                        </Alert>
                                    ) : null}

                                    <div className="p-2 mt-4">
                                        <Form
                                            onSubmit={(e) => {
                                                e.preventDefault()
                                                formik.handleSubmit()
                                                return false
                                            }}
                                            action="#"
                                        >
                                            <div className="mb-3">
                                                <Label htmlFor="username" className="form-label">
                                                    {t('form.login.field.username')}
                                                </Label>
                                                <Input
                                                    id="username"
                                                    name="username"
                                                    className="form-control"
                                                    placeholder={t('form.login.placeholder.username')}
                                                    type="text"
                                                    onChange={(e) => {
                                                        const valueWithoutSpaces = e.target.value.replace(/\s/g, '')
                                                        formik.setFieldValue('username', valueWithoutSpaces)
                                                    }}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values.username || ''}
                                                    invalid={formik.touched.username && formik.errors.username ? true : false}
                                                    onKeyDown={(e) => {
                                                        if (e.key === ' ') {
                                                            e.preventDefault()
                                                        }
                                                    }}
                                                />
                                                {formik.touched.username && formik.errors.username ? (
                                                    <FormFeedback type="invalid">{formik.errors.username}</FormFeedback>
                                                ) : null}
                                            </div>

                                            <div className="mb-3">
                                                <div className="d-flex justify-content-between">
                                                    <Label htmlFor="password" className="form-label">
                                                        {t('app.common.password')}
                                                    </Label>
                                                    <Link to="/forgot-password" className="text-muted">
                                                        {t('app.common.password.forgotPassword')}
                                                    </Link>
                                                </div>
                                                <div className="position-relative auth-pass-inputgroup mb-3">
                                                    <Input
                                                        id="password"
                                                        name="password"
                                                        value={formik.values.password || ''}
                                                        type={obscurePassword ? 'password' : 'text'}
                                                        className="form-control pe-5"
                                                        placeholder={t('app.common.password.placeholder')}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        invalid={formik.touched.password && formik.errors.password ? true : false}
                                                    />
                                                    {formik.touched.password && formik.errors.password ? (
                                                        <FormFeedback type="invalid">{formik.errors.password}</FormFeedback>
                                                    ) : null}

                                                    <ObscurePassword
                                                        errors={formik.errors}
                                                        obscurePassword={obscurePassword}
                                                        setObscurePassword={setObscurePassword}
                                                    />
                                                </div>
                                            </div>

                                            <div className="mt-4">
                                                <Button
                                                    color="success"
                                                    className="btn btn-success w-100"
                                                    type="submit"
                                                    disabled={isPending}
                                                    data-testid="login-submit"
                                                >
                                                    {isPending ? <Spinner size="sm" /> : t('button.login')}
                                                </Button>
                                            </div>
                                        </Form>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    <div className="mt-4 text-center">
                        <Link to="/register" className="text-primary text-decoration-underline">
                            {t('app.common.register')}
                        </Link>
                    </div>
                </Container>
            </div>
        </AuthLayout>
    )
}

export default Login
